import p5 from "p5";

// Define the Star class
class Star {
  constructor(p) {
    this.p = p;

    // Determine if the star is a shooting star (5% chance)
    this.isShootingStar = this.p.random(1) < 0.05;

    if (this.isShootingStar) {
      // Initialize initial delay for shooting star
      this.isDelaying = true;
      this.initialDelay = this.p.random(1, 5); // Random initial delay between 1 to 5 seconds
    }

    this.reset();
  }

  reset() {
    // Random starting position within the element
    this.xOrigin = this.p.random(window.innerWidth);
    this.yOrigin = this.p.random(window.innerHeight);
    this.x = this.xOrigin;
    this.y = this.yOrigin;
    this.size = this.p.random(0.2, 2.5);

    if (this.isShootingStar) {
      // Shooting star properties
      let angle = this.p.random(this.p.PI, 3.4); // Angle between π and 3.4 radians
      this.direction = this.p.createVector(
        this.p.cos(angle),
        this.p.sin(angle)
      );
      this.maxDistance = this.p.random(100, window.innerWidth);

      // Calculate end position
      this.endX = this.xOrigin + this.direction.x * this.maxDistance;
      this.endY = this.yOrigin + this.direction.y * this.maxDistance;

      // Easing time parameter
      this.t = 0;
      this.duration = this.p.random(0.8, 3); // Duration in seconds

      // Brightness starts at zero, peaks at maximum speed
      this.brightness = 0;

      // For trail effect
      this.trail = [];
      // Make trail length proportional to maxDistance
      this.trailLength = Math.floor(this.maxDistance / 10); // Adjust divisor as needed

      // For reset delay
      this.isWaitingToReset = false;
      this.resetDelay = 0;

      // Initialize previous position
      this.xPrev = this.x;
      this.yPrev = this.y;
    } else {
      // Static star properties
      this.brightness = this.p.random(150, 255); // Initial brightness
      this.targetBrightness = this.p.random(0, 255); // Next target brightness
      this.brightnessChangeDuration = this.p.random(0.1, 1); // Duration to reach the target brightness
      this.brightnessChangeTimer = 0; // Timer to track the interpolation
      this.startBrightness = this.brightness; // Starting brightness for interpolation
    }
  }

  update(deltaTime) {
    if (this.isShootingStar) {
      if (this.isDelaying) {
        // Decrease initial delay timer
        this.initialDelay -= deltaTime / 1000; // Convert deltaTime to seconds
        if (this.initialDelay <= 0) {
          this.isDelaying = false; // Start the animation
        }
      } else if (this.isWaitingToReset) {
        // Decrease reset delay timer
        this.resetDelay -= deltaTime / 1000;
        if (this.resetDelay <= 0) {
          // Reset the star
          this.reset();
        }
      } else {
        // Update time parameter based on frame time and duration
        this.t += deltaTime / (1000 * this.duration);

        if (this.t >= 1) {
          // Start waiting to reset after a random delay
          this.isWaitingToReset = true;
          this.resetDelay = this.p.random(1, 5); // Random delay between 1 to 5 seconds
          this.trail = []; // Clear the trail
        } else {
          // Easing function for acceleration and deceleration
          let easedT;
          if (this.t < 0.5) {
            easedT = 2 * this.t * this.t;
          } else {
            easedT = -1 + (4 - 2 * this.t) * this.t;
          }

          // Compute derivative of easing function for speed
          let speed;
          if (this.t < 0.5) {
            speed = 4 * this.t;
          } else {
            speed = 4 - 4 * this.t;
          }
          let normalizedSpeed = speed / 2;

          // Store previous position
          let xPrev = this.x;
          let yPrev = this.y;

          // Update position based on eased time
          this.x = this.xOrigin + (this.endX - this.xOrigin) * easedT;
          this.y = this.yOrigin + (this.endY - this.yOrigin) * easedT;

          // Adjust brightness based on speed
          this.brightness = 255 * normalizedSpeed;

          // Compute distance moved
          let dx = this.x - xPrev;
          let dy = this.y - yPrev;
          let distance = Math.sqrt(dx * dx + dy * dy);

          // Define maximum segment length
          let maxSegmentLength = 5; // Adjust as needed

          // Calculate number of segments
          let segments = Math.ceil(distance / maxSegmentLength);

          // Interpolate additional points
          for (let i = 1; i <= segments; i++) {
            let t = i / segments;
            let xi = xPrev + dx * t;
            let yi = yPrev + dy * t;
            this.trail.push({
              x: xi,
              y: yi,
              brightness: this.brightness,
            });
            if (this.trail.length > this.trailLength) {
              this.trail.shift();
            }
          }
        }
      }
    } else {
      // Static star twinkling effect using brightness interpolation
      // Update the timer
      this.brightnessChangeTimer += deltaTime / 1000;

      if (this.brightnessChangeTimer >= this.brightnessChangeDuration) {
        // Reset timer and choose a new target brightness and duration
        this.brightnessChangeTimer = 0;
        this.brightnessChangeDuration = this.p.random(0.1, 1);
        this.startBrightness = this.brightness;
        this.targetBrightness = this.p.random(0, 255);
      }

      // Interpolate brightness with easing (using Smoothstep)
      let t =
        this.brightnessChangeTimer / this.brightnessChangeDuration;
      t = t * t * (3 - 2 * t); // Smoothstep for smoother transitions
      this.brightness = this.p.lerp(
        this.startBrightness,
        this.targetBrightness,
        t
      );
    }
  }

  draw(parallaxOffsetY = 0) {
    if (
      this.isShootingStar &&
      !this.isDelaying &&
      !this.isWaitingToReset
    ) {
      // Adjust position based on parallax
      let yPos = this.y + parallaxOffsetY * 0.5; // Adjust the factor as needed

      // Draw the shooting star and its trail
      this.p.noStroke();
      this.p.fill(255, this.brightness);
      this.p.ellipse(this.x, yPos, this.size);

      for (let i = 0; i < this.trail.length; i++) {
        let pos = this.trail[i];
        let opacity = pos.brightness * (i / this.trail.length);
        let size = this.size * (i / this.trail.length);
        let trailYPos = pos.y + parallaxOffsetY * 0.5;
        this.p.fill(255, opacity);
        this.p.ellipse(pos.x, trailYPos, size);
      }
    } else if (!this.isShootingStar) {
      // Adjust position based on parallax
      let yPos = this.y + parallaxOffsetY * 0.01; // Adjust the factor as needed

      // Draw static star with a glow effect
      this.p.noStroke();
      this.p.fill(255, this.brightness);
      this.p.ellipse(this.x, yPos, this.size);

      // Optional: Add a subtle glow
      this.p.fill(255, this.brightness / 2);
      this.p.ellipse(this.x, yPos, this.size * 2);
    }
  }
}

// Define a function to create a p5 instance and attach it to a specified target element
function createStarCanvas(targetElementId) {
  if (!targetElementId) return;

  let scrollOffset = 0;

  // Listen to scroll events
  window.addEventListener('scroll', () => {
    scrollOffset = window.scrollY || window.pageYOffset;
  });

  let sketch = function (p) {
    let stars = [];
    let numStars = 150; // Adjust this number based on performance

    p.setup = function () {
      let targetElement = document.getElementById(targetElementId);
      if (!targetElement) {
        console.error(
          `Element with ID '${targetElementId}' not found.`
        );
        return;
      }

      let canvas = p.createCanvas(
        window.innerWidth,
        window.innerHeight
      );
      canvas.parent(targetElement);

      p.noStroke();

      // Instantiate Star objects
      for (let i = 0; i < numStars; i++) {
        stars.push(new Star(p));
      }
    };

    p.draw = function () {
      p.clear();

      let deltaTime = p.deltaTime;
      let parallaxOffsetY = scrollOffset;

      for (let star of stars) {
        star.update(deltaTime);
        star.draw(parallaxOffsetY);
      }
    };

    p.windowResized = function () {
      p.resizeCanvas(window.innerWidth, window.innerHeight);
    };
  };

  // Create a new p5 instance with the sketch
  new p5(sketch);
}

// Call the function to create star canvases on specific elements
createStarCanvas("MainContent");
createStarCanvas("Hero");
